import { SubmitEvent, useEffect, useMemo, useState } from 'react';
import { ApiError, ValidationError } from '@connect-x-app/shared-domain-models';
import i18n from '@connect-x-app/shared-translations';
import { ValidatorResponse } from '@connect-x-app/shared-utils';
import { cloneDeep, isEmpty, isEqualWith } from 'lodash';

/**
 * FormValidationErrors type explanation:
 * 1. We check to see if the value of property Key is a primitive, if it is, we just require a validator response (IValidatorResponse).
 * 2. We check if the value of property Key is an array, if it is, we proceed to 3, else to 5
 * 3. We check if the Type of the element of the array, using infer, is a Primitive.
 *    If the value is not a Primitive, proceed to 4, otherwise, we just require a list of validator responses (IValidatorResponse[]).
 * 4. If the Array is not a primitive, we use the type we extracted with infer and require an array of FormValidationErrors<InferredArrayType>.
 * 5. If the array is not a primitive, and not an array, it's an object, so we just recursively use FormValidationErrors with the given type.
 */
type Primitive = string | number | boolean;
export type FormValidationErrors<Form = Record<string, unknown>> = {
  // NonNullable so an optional field (`url?: string`) is still treated as a primitive at step 1
  // rather than falling through to the object branch at step 5.
  [Key in keyof Form]?: NonNullable<Form[Key]> extends Primitive // 1.
    ? ValidatorResponse
    : NonNullable<Form[Key]> extends Array<infer TArray> // 2.
      ? TArray extends Primitive // 3.
        ? ValidatorResponse[]
        : Array<FormValidationErrors<TArray>> // 4
      : FormValidationErrors<NonNullable<Form[Key]>>; // 5
};

export type SubmitFormFunction<Form> = (values: Form, setFormValues: (values: Form) => void) => void;
type ValidateFormFunction<Form, FormErrors> = (values: Form) => FormValidationErrors<FormErrors>;

type Params<Form, FormErrors> = {
  error?: ApiError;
  ignoreDirtyCheck?: (values: Form) => Array<keyof Form>;
  initialForm: Form;
  submitForm: SubmitFormFunction<Form>;
  validateForm: ValidateFormFunction<Form, FormErrors>;
};

export type Response<Form, FormErrors> = {
  hasValidationErrors: boolean;
  isDirty: boolean;
  isValid: boolean;
  setAttribute: (value: unknown, name: string) => void;
  setValues: (values: Form) => void;
  submit: (event: SubmitEvent) => boolean;
  submitWithParams: (event: SubmitEvent, params: Partial<Params<Form, FormErrors>>) => boolean;
  validationErrors: FormValidationErrors<FormErrors>;
  values: Form;
};

export const mapToFormValidationErrors = <Form>(error: ApiError): FormValidationErrors<Form> => {
  type ValidationMap = {
    [key: string]: ValidatorResponse | ValidationError | ValidationMap;
  };
  const mapError = (validationError: ValidationError): ValidationMap | ValidatorResponse => {
    if (validationError.children.length > 0) {
      return validationError.children.reduce(
        (acc, child) => ({
          ...acc,
          [child.property]: {
            ...mapError(child),
          },
        }),
        <ValidationMap>{},
      );
    }
    let message: string = i18n.t('ERRORS.VALIDATION.INVALID');
    if (validationError.constraints?.['isNotEmpty']) {
      message = i18n.t('ERRORS.VALIDATION.REQUIRED');
    }
    return { isValid: false, message };
  };

  return Object.keys(error.validationErrors ?? {}).reduce(
    (acc, key) => ({
      ...acc,
      [key]: {
        // ! should be safe since we fallback to empty object if error.validationErrors is undefined
        ...mapError(error.validationErrors![key]),
      },
    }),
    <FormValidationErrors<Form>>{},
  );
};

const isValidatorResponse = (object: object): object is ValidatorResponse => Object.keys(object).includes('isValid');

export const hasValidationErrors = (errors?: FormValidationErrors): boolean => {
  if (isEmpty(errors)) {
    return false;
  }
  if (Array.isArray(errors)) {
    return errors.some(hasValidationErrors);
  }
  if (typeof errors === 'object') {
    if (isValidatorResponse(errors)) {
      return !errors.isValid;
    }
    return Object.keys(errors).some(key => hasValidationErrors(errors[key]));
  }
  return false;
};

export const useForm = <Form, FormErrors = Form>(params: Params<Form, FormErrors>): Response<Form, FormErrors> => {
  const { error, ignoreDirtyCheck = () => [], initialForm, submitForm, validateForm } = params;
  const [formValues, setFormValues] = useState<Form>(initialForm);
  const [validationErrors, setValidationErrors] = useState<FormValidationErrors<FormErrors>>({});

  const submit = (
    event: SubmitEvent,
    sumbitFunction: SubmitFormFunction<Form> = submitForm,
    validateFunction: ValidateFormFunction<Form, FormErrors> = validateForm,
  ): boolean => {
    event.preventDefault();
    const errors = validateFunction(formValues);
    const hasErrors = hasValidationErrors(errors);
    if (!hasErrors) {
      sumbitFunction(formValues, setFormValues);
    }
    setValidationErrors(errors);
    return !hasErrors;
  };

  /**
   * In some cases, you want to use a different submit / validate function than the default one.
   */
  const submitWithParams = (event: SubmitEvent, params: Partial<Params<Form, FormErrors>>): boolean =>
    submit(event, params.submitForm, params.validateForm);

  /**
   * Use this function if the (simple) name of the field matches the name within the form.
   * Do not use it when the field is an array or (part of) a nested object. Use 'setValues' instead.
   *
   * The name of the input field should be equal to the simple property name within the form.
   * E.g. By using this function with '<Input name='title' />', the new value will be set on 'values.title'.
   */
  const setAttribute = (value: unknown, name: string) => {
    const updateFormValue = { ...formValues, [name]: value };
    setFormValues(updateFormValue);
    validateFormAttribute(name, updateFormValue);
  };

  /**
   * Use this function if you cannot change the value with 'setAttribute' because it is (part of) a nested object or an array.
   * If it is a simple value, we recommend to use 'setAttribute' for performance reasons.
   *
   * The name of the input field is not used to set any value here, as the value is set directly in the values
   */
  const setValues = (values: Form) => {
    const newValues = cloneDeep(values);
    setFormValues(newValues);
  };

  const validateFormAttribute = (name: string, form: Form) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    const attributeValidation = (params.validateForm(form)[name] as object) ?? { isValid: true };
    setValidationErrors({ ...validationErrors, [name]: attributeValidation });
  };

  // Map server errors to form validation errors
  useEffect(() => {
    if (error?.validationErrors) {
      setValidationErrors(mapToFormValidationErrors(error));
    }
  }, [error]);

  useEffect(() => {
    const clearValues = () => setFormValues(initialForm);

    setFormValues(initialForm);
    return () => {
      clearValues();
      setValidationErrors({});
    };
  }, [initialForm, setFormValues]);

  const isDirty = useMemo(() => {
    const keysToIgnore = ignoreDirtyCheck(formValues);

    return !isEqualWith(formValues, initialForm, (a, b, key) => {
      if (typeof key === 'string' && keysToIgnore.includes(key as keyof Form)) {
        return true;
      }

      if (typeof a !== typeof b) {
        return a == b;
      }
      return undefined;
    });
  }, [formValues, initialForm, ignoreDirtyCheck]);

  const isValid = useMemo(() => {
    const errors = validateForm(formValues);
    const hasErrors = hasValidationErrors(errors);

    return !hasErrors;
  }, [validateForm, formValues]);

  return {
    hasValidationErrors: hasValidationErrors(validationErrors),
    isDirty,
    isValid,
    setAttribute,
    setValues,
    submit,
    submitWithParams,
    validationErrors,
    values: formValues,
  };
};
